<template>
  <div :class="$style.rating">
    <div :class="[$style.rating__stars, $style.stars]">
      <IconReviewStar
        :class="$style.stars__item"
        :style="{
          width: sizePx,
          height: sizePx,
          fontSize: sizePx,
          lineHeight: sizePx,
          marginLeft: index > 0 ? `${gap}px` : 0,
        }"
        v-for="(rateOfStar, index) in rates"
        :key="index"
        :rate="rateOfStar"
      />
    </div>
    <slot name="rate" v-if="!hideRate">
      <p
        :class="$style.rating__point"
        :style="{
          fontSize: `${(size / 8) * 7}px`,
          marginLeft: `${(size / 4) * 3}px`,
        }"
      >
        {{ rateString }}
      </p>
    </slot>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import IconReviewStar from '@/components/icons/IconReviewStar.vue'

const DEFAULT_STEP = 0.1

export default defineComponent({
  name: 'RatingStars',
  components: {
    IconReviewStar,
  },
  props: {
    size: {
      type: Number,
      default: 16,
    },
    gap: {
      type: Number,
      default: 8,
    },
    max: {
      type: Number,
      default: 5,
    },
    rate: {
      type: Number,
      default: 0,
    },
    step: {
      type: Number,
      default: DEFAULT_STEP,
    },
    hideRate: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    sizePx(): string {
      return `${this.size}px`
    },
    normalizedMax(): number {
      return Math.floor(this.max)
    },
    normalizedRate(): number {
      return Math.min(Math.max(this.rate, 0), this.normalizedMax)
    },
    normalizedStep(): number {
      return this.step >= 0 ? Math.min(this.step, 1) : DEFAULT_STEP
    },
    rates(): number[] {
      const fraction =
        Math.round((this.normalizedRate % 1) / this.normalizedStep) *
        this.normalizedStep

      return Array.from({ length: this.normalizedMax }, (_, index) => {
        if (index + 1 <= this.normalizedRate) {
          return 1
        }
        return index < this.normalizedRate ? fraction : 0
      })
    },
    rateString(): string {
      return this.rate.toFixed(1)
    },
  },
})
</script>

<style lang="scss" module>
.stars {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
}

.rating {
  display: inline-flex;
  align-items: center;

  &__point {
    color: $color-text-and-object-accent-orange;
    font-weight: bold;
  }
}
</style>
